import { useEffect, useState } from 'react';

import {
  fromCronString,
  parseRecipients,
  serialiseRecipients,
  toCronString,
} from './scheduleEmailHelpers';
import { Attachment, Tag } from '../../../../../@types';
import { AutocompleteTagType } from '../../../../../components/organisms/Tags';
import { ScheduleType, WarningMessages } from '../../../../../configs/enums';
import {
  useAddScheduleReport,
  useGetScheduleReportDataByReportId,
  useSendSavedReportTestEmail,
  useUpdateReportScheduleEmailByReportId,
} from '../../../../../queries/report-query';
import { isDropDownEmpty } from '../../../../../utils/common';
import { getScheduleTimeUsingType, getScheduleTypeUsingTime } from '../../../../../utils/report';
import { toast } from '../../../../../utils/toast';

const NO_SCHEDULE_TYPE = '-1';
const TEST_EMAIL_IN_PROGRESS = 'Email report generation in progress!';
const TEST_EMAIL_STATUS_TIMEOUT_MS = 3000;

export const useScheduleEmailForm = (
  reportId: number | undefined,
  attachmentTypes: Attachment[],
  handleRefetch: (refetch: boolean) => void
) => {
  // `number | undefined`, not `number`: `attachmentTypes` is empty on the first
  // render, so the old annotation was a lie TS could not see through the index
  // access — and every consumer then had to defend against it anyway.
  const [attachmentType, setAttachmentType] = useState<number | undefined>(attachmentTypes[0]?.id);
  /** The id the create call returned, so a second save updates it rather than
   *  creating a duplicate while the refetch is still in flight. */
  const [createdScheduleId, setCreatedScheduleId] = useState<number>();
  const [emailBody, setEmailBody] = useState('');
  const [subject, setSubject] = useState('');
  const [scheduleValue, setScheduleValue] = useState('');
  const [scheduleType, setScheduleType] = useState(NO_SCHEDULE_TYPE);
  const [isActive, setIsActive] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<(AutocompleteTagType | string)[]>([]);
  const [newEmails, setNewEmails] = useState<Tag[]>([]);
  const [testEmailStatus, setTestEmailStatus] = useState('');

  const createScheduleReport = useAddScheduleReport();
  const updateScheduleReport = useUpdateReportScheduleEmailByReportId(reportId?.toString() || '');
  const scheduleQuery = useGetScheduleReportDataByReportId(reportId?.toString() || '');
  const sendTestEmail = useSendSavedReportTestEmail();

  useEffect(() => {
    scheduleQuery.refetch();
    // The query is `enabled: false`, so this is the only thing that fetches it.
    // `scheduleQuery` is a fresh object every render and would loop if listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    // `useState(attachmentTypes[0]?.id)` only ever sees the first render, and
    // `useGetAttachmentTypes` is a plain enabled query that resolves *after* the
    // dialog has mounted its tabs — so a brand-new schedule was left with no
    // attachment type at all and warned on save with the dropdown looking blank.
    // Adopt the first type once it lands, without overwriting a choice already
    // made by the user or restored from a saved record.
    if (attachmentType === undefined && attachmentTypes.length > 0) {
      setAttachmentType(attachmentTypes[0].id);
    }
  }, [attachmentType, attachmentTypes]);

  useEffect(() => {
    const saved = scheduleQuery.data;
    if (!saved) return;

    const cronValue = toCronString(saved.schedule);
    setAttachmentType(saved.attachmentTypeId);
    setEmailBody(saved.body);
    setSubject(saved.subject);
    setIsActive(saved.isActive);
    setSelectedEmails(parseRecipients(saved.recepients));
    setScheduleType(getScheduleTypeUsingTime(cronValue));
    setScheduleValue(cronValue);
  }, [scheduleQuery.data]);

  useEffect(() => {
    if (testEmailStatus === '' || testEmailStatus === TEST_EMAIL_IN_PROGRESS) return;
    const timer = setTimeout(() => setTestEmailStatus(''), TEST_EMAIL_STATUS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [testEmailStatus]);

  const validate = (): string | undefined => {
    // `?? ''` rather than `.toString()`: with no attachment types configured the
    // id is undefined, and the old unguarded call threw instead of warning.
    if (isDropDownEmpty(attachmentType?.toString() ?? ''))
      return WarningMessages.AttachmentTypeEmpty;
    if (selectedEmails.length === 0) return WarningMessages.RecipientsEmpty;
    if (!subject) return WarningMessages.SubjectEmpty;
    if (!emailBody) return WarningMessages.EmailBodyEmpty;
    if (isDropDownEmpty(scheduleType)) return WarningMessages.ScheduleTypeEmpty;
    if (!scheduleValue) return WarningMessages.ScheduleTimeEmpty;
    if (reportId === undefined) return 'Report ID is required';
    return undefined;
  };

  const handleSave = async () => {
    const failure = validate();
    // `attachmentType` is re-checked rather than trusted: `validate` already
    // rejects it, but only this narrows it for the payload below.
    if (failure || reportId === undefined || attachmentType === undefined) {
      toast.warning(failure || 'Report ID is required');
      return;
    }

    const scheduleData = {
      reportId,
      attachmentTypeId: attachmentType,
      recepients: serialiseRecipients(selectedEmails),
      subject,
      body: emailBody,
      schedule: fromCronString(scheduleValue),
      isActive,
    };

    try {
      // `refetch` below is not awaited, so `scheduleQuery.data` can still be
      // empty on an immediate second save. Remembering the created id is what
      // stops that second save from creating a duplicate schedule.
      if (scheduleQuery.data?.id ?? createdScheduleId) {
        await updateScheduleReport.mutateAsync(scheduleData);
      } else {
        const created = await createScheduleReport.mutateAsync(scheduleData);
        setCreatedScheduleId(created?.id);
      }
    } catch (error: unknown) {
      // The footer calls this straight from `onClick`, so a rejected mutation
      // used to surface as an unhandled rejection and nothing else: the dialog
      // sat there looking as though the save had worked.
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.warning(axiosError?.response?.data?.message || 'Saving the schedule failed');
      return;
    }

    scheduleQuery.refetch();
    handleRefetch(true);
  };

  const handleSendTestEmail = async () => {
    if (!subject) {
      setTestEmailStatus('Test email subject is required');
      return;
    }
    if (!emailBody) {
      setTestEmailStatus('Test email body is required');
      return;
    }
    if (reportId === undefined) {
      setTestEmailStatus('Report ID is required');
      return;
    }
    // The save path rejects a missing attachment type; without this the test
    // email posted `attachmentTypeId: undefined` instead.
    if (attachmentType === undefined) {
      setTestEmailStatus(WarningMessages.AttachmentTypeEmpty);
      return;
    }

    setTestEmailStatus(TEST_EMAIL_IN_PROGRESS);
    try {
      const response = await sendTestEmail.mutateAsync({
        reportId,
        attachmentTypeId: attachmentType,
        subject,
        emailBody,
      });
      setTestEmailStatus(response.message);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      setTestEmailStatus(axiosError?.response?.data?.message || 'Test email sending failed!');
    }
  };

  const onChangeScheduleType = (type: string) => {
    setScheduleType(type);
    setScheduleValue(getScheduleTimeUsingType(type));
  };

  return {
    attachmentType,
    setAttachmentType,
    emailBody,
    setEmailBody,
    subject,
    setSubject,
    scheduleValue,
    setScheduleValue,
    scheduleType,
    onChangeScheduleType,
    /**
     * Gates the cron editor. Presets own their own value and already say what they
     * do in the dropdown label, so they get no editor at all rather than a disabled
     * one restating it. Safe for saved schedules: `getScheduleTypeUsingTime`
     * defaults to `Custom`, so any cron that is not one of the four presets
     * resolves back to Custom and its editor.
     */
    isCustomSchedule: scheduleType === ScheduleType.Custom,
    isActive,
    setIsActive,
    selectedEmails,
    setSelectedEmails,
    newEmails,
    setNewEmails,
    testEmailStatus,
    handleSave,
    handleSendTestEmail,
    isExistingSchedule: Boolean(scheduleQuery.data?.id ?? createdScheduleId),
    isSaving: createScheduleReport.isPending || updateScheduleReport.isPending,
    isSendingTestEmail: sendTestEmail.isPending,
  };
};
